import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { Node, NodeProps } from '@xyflow/react';
import type { DiseaseNodeData } from '@/utils/ontologyTree/types';

export type DiseaseFlowNode = Node<DiseaseNodeData, 'disease'>;

const C = {
  border: '#4a90d9',
  borderActive: '#2c6fad',
  label: '#4a90d9',
  text: '#1a2e44',
  cuiBg: '#e8f2ff',
  cuiText: '#2c6fad',
  btnBg: '#f0f7ff',
  btnBgHover: '#dbeeff',
} as const;

export const DiseaseNode = memo(function DiseaseNode({ id, data, selected }: NodeProps<DiseaseFlowNode>) {
  const { label, cui, hasChildren, isExpanded, isLoading, numDescendants, onToggle, onCollapse, onNavigate } =
    data;

  return (
    <div
      style={{
        position: 'relative',
        background: '#fff',
        border: `2px solid ${selected ? C.borderActive : C.border}`,
        borderRadius: 10,
        padding: '10px 14px',
        minWidth: 220,
        maxWidth: 300,
        boxShadow: selected
          ? '0 0 0 3px rgba(74,144,217,0.2), 0 4px 12px rgba(0,0,0,0.1)'
          : '0 2px 6px rgba(0,0,0,0.07)',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        userSelect: 'none',
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: C.border, width: 8, height: 8 }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            flex: 1,
            fontSize: 13,
            fontWeight: 600,
            color: C.text,
            lineHeight: 1.35,
            wordBreak: 'break-word',
            minWidth: 0,
          }}
          title={label}
        >
          {label.length > 48 ? `${label.slice(0, 46)}…` : label}
        </span>

        {hasChildren && !isExpanded && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onToggle(id);
            }}
            disabled={isLoading}
            title={`Expand${numDescendants ? ` (${numDescendants} descendants)` : ''}`}
            aria-label="Expand node"
            style={{
              padding: '3px 9px',
              fontSize: 11,
              fontWeight: 600,
              border: `1px solid ${C.border}`,
              borderRadius: 5,
              background: '#fff',
              color: C.borderActive,
              cursor: isLoading ? 'wait' : 'pointer',
              flexShrink: 0,
              transition: 'background 0.12s',
              lineHeight: 1.5,
            }}
          >
            {isLoading ? '…' : '+'}
          </button>
        )}
        {isExpanded && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onCollapse(id);
            }}
            title="Collapse this node"
            aria-label="Collapse node"
            style={{
              padding: '3px 9px',
              fontSize: 11,
              fontWeight: 500,
              border: `1px solid ${C.border}`,
              borderRadius: 5,
              background: C.btnBg,
              color: C.borderActive,
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'background 0.12s',
              lineHeight: 1.5,
            }}
            onMouseOver={e => (e.currentTarget.style.background = C.btnBgHover)}
            onMouseOut={e => (e.currentTarget.style.background = C.btnBg)}
          >
            Collapse
          </button>
        )}

        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            onNavigate(id);
          }}
          title="View in table"
          style={{
            padding: '3px 9px',
            fontSize: 11,
            fontWeight: 500,
            border: `1px solid ${C.border}`,
            borderRadius: 5,
            background: C.btnBg,
            color: C.borderActive,
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'background 0.12s',
            lineHeight: 1.5,
          }}
          onMouseOver={e => (e.currentTarget.style.background = C.btnBgHover)}
          onMouseOut={e => (e.currentTarget.style.background = C.btnBg)}
        >
          View →
        </button>
      </div>

      {cui && (
        <span
          style={{
            display: 'inline-block',
            marginTop: 6,
            fontSize: 10,
            fontWeight: 500,
            color: C.cuiText,
            background: C.cuiBg,
            borderRadius: 4,
            padding: '2px 6px',
          }}
        >
          {cui}
        </span>
      )}

      <Handle type="source" position={Position.Right} style={{ background: C.border, width: 8, height: 8 }} />
    </div>
  );
});
